/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit2, DollarSign, ChevronDown, ChevronRight, Save, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = ['ALL', 'DIRECTION', 'ART', 'COSTUME_WARDROBE', 'CASTING', 'PRODUCTION',
  'MAKEUP_HAIR', 'VFX', 'LOCATIONS', 'SFX_STUNTS', 'SOUND', 'CAMERA', 'EDITING'];

const CATEGORIES = ['PROP', 'COSTUME', 'ART_SET_DRESSING', 'VFX', 'STUNT',
  'SFX_SPECIAL_EFFECTS', 'VEHICLE', 'ANIMAL', 'EXTRA_BACKGROUND', 'MAKEUP_HAIR', 'SOUND_MUSIC', 'PRODUCTION_NOTE'];

const UNITS = ['ea', 'days', 'hrs', 'wks', 'ft', 'sqft', 'kg', 'lbs', 'set', 'lot'];

const DEPT_COLORS = {
  ART: 'text-purple-400', COSTUME_WARDROBE: 'text-yellow-400', CASTING: 'text-blue-400',
  MAKEUP_HAIR: 'text-pink-400', VFX: 'text-cyan-400', SFX_STUNTS: 'text-orange-400',
  SOUND: 'text-teal-400', PRODUCTION: 'text-green-400', DIRECTION: 'text-red-400',
  CAMERA: 'text-indigo-400', EDITING: 'text-violet-400', LOCATIONS: 'text-lime-400'
};

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EmptyRow = ({ onAdd }) => (
  <tr>
    <td colSpan={8} className="p-4 text-center">
      <Button variant="ghost" onClick={onAdd} className="text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)] text-sm" data-testid="add-first-line-btn">
        <Plus className="h-4 w-4 mr-1" /> Add budget line
      </Button>
    </td>
  </tr>
);

const BLANK_LINE = { department: 'PRODUCTION', category: '', description: '', quantity: 1, unit: 'ea', unit_cost: 0, notes: '' };

export default function BudgetPage({ embedded = false }) {
  const params = useParams();
  const projectId = params.projectId;
  const navigate = useNavigate();
  const { token } = useAuth();

  const [lines, setLines] = useState([]);
  const [summary, setSummary] = useState({ grand_total: 0, by_department: {} });
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [adding, setAdding] = useState(false);
  const [newLine, setNewLine] = useState({ ...BLANK_LINE });
  const [filterDept, setFilterDept] = useState('ALL');
  const [collapsed, setCollapsed] = useState({});
  const [projectTitle, setProjectTitle] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchProjectTitle = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects`, { headers });
      const proj = r.data.find(p => p.id === projectId);
      if (proj) setProjectTitle(proj.title);
    } catch (err) { console.error('Fetch project title error:', err); }
  }, [projectId, headers]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [budgetRes, summaryRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/budget`, { headers }),
        axios.get(`${API}/projects/${projectId}/budget/summary`, { headers })
      ]);
      setLines(budgetRes.data.lines || []);
      setSummary(summaryRes.data);
    } catch {
      toast.error('Failed to load budget');
    } finally {
      setLoading(false);
    }
  }, [projectId, headers]);

  useEffect(() => {
    fetchAll();
    fetchProjectTitle();
  }, [fetchAll, fetchProjectTitle]);

  const handleAdd = async () => {
    if (!newLine.description.trim()) { toast.error('Description required'); return; }
    try {
      const payload = { ...newLine, unit_cost: parseFloat(newLine.unit_cost) || 0, quantity: parseFloat(newLine.quantity) || 1 };
      delete payload.category;
      if (newLine.category) payload.category = newLine.category;
      await axios.post(`${API}/projects/${projectId}/budget`, payload, { headers });
      toast.success('Line added');
      setAdding(false);
      setNewLine({ ...BLANK_LINE });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const payload = { ...editDraft, unit_cost: parseFloat(editDraft.unit_cost) || 0, quantity: parseFloat(editDraft.quantity) || 1 };
      if (!payload.category) delete payload.category;
      await axios.put(`${API}/projects/${projectId}/budget/${id}`, payload, { headers });
      toast.success('Updated');
      setEditingId(null);
      fetchAll();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this budget line?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/budget/${id}`, { headers });
      toast.success('Deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const displayed = filterDept === 'ALL' ? lines : lines.filter(l => l.department === filterDept);
  const grouped = displayed.reduce((acc, l) => { (acc[l.department] = acc[l.department] || []).push(l); return acc; }, {});

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[var(--cb-bg)] text-[var(--cb-text)]'}>
      {/* Header */}
      {!embedded && (
      <div className="border-b border-[var(--cb-border)] bg-[var(--cb-bg)] sticky top-0 z-20 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/projects/${projectId}/breakdown`)}
              className="text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]">
              <ArrowLeft className="h-4 w-4 mr-1" /> Breakdown
            </Button>
            <div className="h-4 w-px bg-[var(--cb-border)]" />
            <DollarSign className="h-5 w-5 text-[var(--cb-accent)]" />
            <div>
              <h1 className="font-['DM_Serif_Display'] text-xl text-[var(--cb-text)]">Budget</h1>
              <p className="text-xs text-[var(--cb-text-sec)]">{projectTitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="w-40 bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm" data-testid="dept-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                {DEPARTMENTS.map(d => <SelectItem key={d} value={d} className="text-[var(--cb-text)]">{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={() => setAdding(true)} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="add-line-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Line
            </Button>
          </div>
        </div>

        {/* Totals bar */}
        <div className="flex gap-6 mt-3 overflow-x-auto pb-1">
          <div className="shrink-0 bg-[var(--cb-surface-h)] border border-[var(--cb-border)] rounded-lg px-4 py-2" data-testid="grand-total">
            <div className="text-xs text-[var(--cb-text-sec)]">Grand Total</div>
            <div className="text-lg font-bold text-[var(--cb-accent)]">{fmt(summary.grand_total)}</div>
          </div>
          {Object.entries(summary.by_department || {}).map(([dept, amt]) => (
            <div key={dept} className="shrink-0 bg-[var(--cb-surface-h)] border border-[var(--cb-border)] rounded-lg px-3 py-2" data-testid={`dept-total-${dept}`}>
              <div className={`text-xs ${DEPT_COLORS[dept] || 'text-[var(--cb-text-sec)]'}`}>{dept.replace('_', ' ')}</div>
              <div className="text-sm font-semibold text-[var(--cb-text)]">{fmt(amt)}</div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Totals bar - always show when embedded */}
      {embedded && (
        <div className="flex gap-4 px-6 py-4 overflow-x-auto border-b border-[var(--cb-border)]">
          <div className="shrink-0 bg-[var(--cb-surface-h)] border border-[var(--cb-border)] rounded-lg px-4 py-2">
            <div className="text-xs text-[var(--cb-text-sec)]">Grand Total</div>
            <div className="text-lg font-bold text-[var(--cb-accent)]">{fmt(summary.grand_total)}</div>
          </div>
          {Object.entries(summary.by_department || {}).map(([dept, amt]) => (
            <div key={dept} className="shrink-0 bg-[var(--cb-surface-h)] border border-[var(--cb-border)] rounded-lg px-3 py-2">
              <div className={`text-xs ${DEPT_COLORS[dept] || 'text-[var(--cb-text-sec)]'}`}>{dept.replace(/_/g, ' ')}</div>
              <div className="text-sm font-semibold text-[var(--cb-text)]">{fmt(amt)}</div>
            </div>
          ))}
          <div className="shrink-0 flex items-center">
            <Button size="sm" onClick={() => setAdding(true)} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black h-8" data-testid="add-line-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Line
            </Button>
          </div>
        </div>
      )}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-12 text-[var(--cb-text-sec)]">Loading budget...</div>
        ) : (
          <div className="space-y-4">
            {/* Add row inline */}
            {adding && (
              <Card className="bg-[var(--cb-surface-h)] border-[var(--cb-accent)] p-4" data-testid="add-line-form">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  <Select value={newLine.department} onValueChange={v => setNewLine(p => ({...p, department: v}))}>
                    <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm">
                      <SelectValue placeholder="Department" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                      {DEPARTMENTS.filter(d => d !== 'ALL').map(d => <SelectItem key={d} value={d} className="text-[var(--cb-text)]">{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={newLine.category || '__none__'} onValueChange={v => setNewLine(p => ({...p, category: v === '__none__' ? '' : v}))}>
                    <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm">
                      <SelectValue placeholder="Category (opt)" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                      <SelectItem value="__none__" className="text-[var(--cb-text-sec)]">— None —</SelectItem>
                      {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-[var(--cb-text)]">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={newLine.description} onChange={e => setNewLine(p => ({...p, description: e.target.value}))}
                    placeholder="Description *" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm col-span-2" data-testid="new-line-description" />
                </div>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                  <Input type="number" value={newLine.quantity} onChange={e => setNewLine(p => ({...p, quantity: e.target.value}))}
                    placeholder="Qty" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm" data-testid="new-line-qty" />
                  <Select value={newLine.unit} onValueChange={v => setNewLine(p => ({...p, unit: v}))}>
                    <SelectTrigger className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                      {UNITS.map(u => <SelectItem key={u} value={u} className="text-[var(--cb-text)]">{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={newLine.unit_cost} onChange={e => setNewLine(p => ({...p, unit_cost: e.target.value}))}
                    placeholder="Unit Cost" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm" data-testid="new-line-cost" />
                  <div className="flex items-center text-[var(--cb-accent)] font-semibold text-sm col-span-1">
                    = {fmt(parseFloat(newLine.quantity || 1) * parseFloat(newLine.unit_cost || 0))}
                  </div>
                  <Input value={newLine.notes} onChange={e => setNewLine(p => ({...p, notes: e.target.value}))}
                    placeholder="Notes" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-8 text-sm col-span-2" />
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={handleAdd} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="save-new-line-btn">
                    <Save className="h-3 w-3 mr-1" /> Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewLine({ ...BLANK_LINE }); }} className="text-[var(--cb-text-sec)]">
                    <X className="h-3 w-3 mr-1" /> Cancel
                  </Button>
                </div>
              </Card>
            )}

            {/* Grouped tables */}
            {Object.keys(grouped).length === 0 && !adding && (
              <Card className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] p-12 text-center">
                <DollarSign className="h-12 w-12 text-[var(--cb-border)] mx-auto mb-3" />
                <p className="text-[var(--cb-text-sec)] mb-4">No budget lines yet</p>
                <Button onClick={() => setAdding(true)} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="empty-add-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add First Line
                </Button>
              </Card>
            )}

            {Object.entries(grouped).map(([dept, deptLines]) => {
              const deptTotal = deptLines.reduce((s, l) => s + l.total_cost, 0);
              const isCollapsed = collapsed[dept];
              return (
                <Card key={dept} className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] overflow-hidden" data-testid={`dept-group-${dept}`}>
                  <div
                    className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[var(--cb-bg)] border-b border-[var(--cb-border)]"
                    onClick={() => setCollapsed(p => ({...p, [dept]: !p[dept]}))}
                  >
                    <div className="flex items-center gap-3">
                      {isCollapsed ? <ChevronRight className="h-4 w-4 text-[var(--cb-text-sec)]" /> : <ChevronDown className="h-4 w-4 text-[var(--cb-text-sec)]" />}
                      <span className={`font-semibold text-sm ${DEPT_COLORS[dept] || 'text-[var(--cb-text)]'}`}>{dept.replace(/_/g, ' ')}</span>
                      <Badge variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] text-xs">{deptLines.length}</Badge>
                    </div>
                    <span className="font-bold text-[var(--cb-accent)] text-sm">{fmt(deptTotal)}</span>
                  </div>
                  {!isCollapsed && (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[var(--cb-bg)]">
                          <th className="text-left p-2 pl-4 text-[var(--cb-text-sec)] font-normal text-xs w-36">Category</th>
                          <th className="text-left p-2 text-[var(--cb-text-sec)] font-normal text-xs">Description</th>
                          <th className="text-left p-2 text-[var(--cb-text-sec)] font-normal text-xs w-16">Qty</th>
                          <th className="text-left p-2 text-[var(--cb-text-sec)] font-normal text-xs w-16">Unit</th>
                          <th className="text-left p-2 text-[var(--cb-text-sec)] font-normal text-xs w-24">Unit Cost</th>
                          <th className="text-left p-2 text-[var(--cb-text-sec)] font-normal text-xs w-24">Total</th>
                          <th className="text-left p-2 text-[var(--cb-text-sec)] font-normal text-xs">Notes</th>
                          <th className="p-2 w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {deptLines.map(line => (
                          editingId === line.id ? (
                            <tr key={line.id} className="border-t border-[var(--cb-border)] bg-[var(--cb-bg)]" data-testid={`edit-row-${line.id}`}>
                              <td className="p-2 pl-4">
                                <Select value={editDraft.category || '__none__'} onValueChange={v => setEditDraft(p => ({...p, category: v === '__none__' ? '' : v}))}>
                                  <SelectTrigger className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-32">
                                    <SelectValue placeholder="Category" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                                    <SelectItem value="__none__" className="text-[var(--cb-text-sec)]">— None —</SelectItem>
                                    {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-[var(--cb-text)] text-xs">{c}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-2"><Input value={editDraft.description} onChange={e => setEditDraft(p => ({...p, description: e.target.value}))} className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs" data-testid="edit-description" /></td>
                              <td className="p-2"><Input type="number" value={editDraft.quantity} onChange={e => setEditDraft(p => ({...p, quantity: e.target.value}))} className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-16" /></td>
                              <td className="p-2">
                                <Select value={editDraft.unit} onValueChange={v => setEditDraft(p => ({...p, unit: v}))}>
                                  <SelectTrigger className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-16"><SelectValue /></SelectTrigger>
                                  <SelectContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)]">
                                    {UNITS.map(u => <SelectItem key={u} value={u} className="text-[var(--cb-text)] text-xs">{u}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="p-2"><Input type="number" value={editDraft.unit_cost} onChange={e => setEditDraft(p => ({...p, unit_cost: e.target.value}))} className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-24" /></td>
                              <td className="p-2 text-[var(--cb-accent)] font-semibold text-xs">{fmt(parseFloat(editDraft.quantity||1)*parseFloat(editDraft.unit_cost||0))}</td>
                              <td className="p-2"><Input value={editDraft.notes||''} onChange={e => setEditDraft(p => ({...p, notes: e.target.value}))} className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs" /></td>
                              <td className="p-2">
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => handleUpdate(line.id)} className="h-7 w-7 p-0 text-green-400 hover:text-green-300" data-testid={`save-edit-${line.id}`}><Save className="h-3 w-3" /></Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 w-7 p-0 text-[var(--cb-text-sec)]"><X className="h-3 w-3" /></Button>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            <tr key={line.id} className="border-t border-[var(--cb-border)] hover:bg-[var(--cb-bg)] group/row" data-testid={`line-row-${line.id}`}>
                              <td className="p-2 pl-4 text-xs text-[var(--cb-text-sec)]">{line.category ? line.category.replace(/_/g,' ') : '—'}</td>
                              <td className="p-2 text-xs text-[var(--cb-text)]">{line.description}</td>
                              <td className="p-2 text-xs text-[var(--cb-text-sec)]">{line.quantity}</td>
                              <td className="p-2 text-xs text-[var(--cb-text-sec)]">{line.unit}</td>
                              <td className="p-2 text-xs text-[var(--cb-text-sec)]">{fmt(line.unit_cost)}</td>
                              <td className="p-2 text-xs font-semibold text-[var(--cb-accent)]">{fmt(line.total_cost)}</td>
                              <td className="p-2 text-xs text-[var(--cb-text-sec)]">{line.notes || '—'}</td>
                              <td className="p-2">
                                <div className="flex gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingId(line.id); setEditDraft({...line}); }} className="h-7 w-7 p-0 text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]" data-testid={`edit-line-${line.id}`}><Edit2 className="h-3 w-3" /></Button>
                                  <Button size="sm" variant="ghost" onClick={() => handleDelete(line.id)} className="h-7 w-7 p-0 text-[var(--cb-text-sec)] hover:text-red-400" data-testid={`delete-line-${line.id}`}><Trash2 className="h-3 w-3" /></Button>
                                </div>
                              </td>
                            </tr>
                          )
                        ))}
                        <tr className="border-t border-[var(--cb-border)]/50">
                          <td colSpan={5} className="p-2 pl-4 text-right text-xs text-[var(--cb-text-sec)] font-medium">Subtotal</td>
                          <td className="p-2 text-xs font-bold text-[var(--cb-accent)]">{fmt(deptTotal)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
